import { HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';

import { ServerRequest } from '../server/server.tokens';

import { removeEmptyObjectProps } from './object-utils';

/**
 * Returns url string without query parameters and hash fragment
 * @param url
 */
export function sanitizeUrl(url: string): string {
  return url?.replace(/[#|?].*$/, '');
}

/**
 * Returns url string without hash fragment
 * @param url
 */
export function removeHash(url: string): string {
  return url?.replace(/#.*$/, '');
}

/**
 * Returns the full server url including protocol, host and path
 * @param request
 */
export function getFullServerUrl({ protocol, hostname, originalUrl }: ServerRequest): string {
  return `${protocol}://${hostname}${originalUrl}`;
}

/**
 * Returns if a given url points to an external website or belongs to current one
 * @param url
 * @param origin
 */
export function isExternalUrl(url: string, origin: string): boolean {
  return url.startsWith('http') && !url.startsWith(origin);
}

/**
 * Return the host origin cross-platform.
 * If ServerRequest is provided take it from there
 * otherwise take it from `location.origin`
 * @param win
 * @param request
 */
export function getHostOrigin(win: Window, request: ServerRequest): string {
  const { protocol, hostname } = request || {};
  if (hostname) {
    return `${protocol}://${hostname}`;
  }

  return win.location.origin;
}

/**
 * Generates a query parameter string from applied object, optionally sanitizing props with empty values.
 * If no encoder applied, the keys and params will be url encoded by using `SimpleUrlEncodingCodec`.
 * @param params
 * @param removeEmpty
 * @param encoder
 */
export function generateQueryParamString(
  params: Record<string, string | number | boolean>,
  removeEmpty = true,
  encoder: HttpUrlEncodingCodec = new SimpleUrlEncodingCodec()
): string {
  const fromObject = removeEmpty ? removeEmptyObjectProps(params) : params;
  return new HttpParams({ fromObject, encoder }).toString();
}

/**
 * A simple url encoder. Usually Angular uses `HttpUrlEncodingCodec`,
 * however after url-encoding it does some re-conversion of characters like "=" or "+".
 * The purpose of this encoder is to keep those characters encoded in case it's required.
 */
export class SimpleUrlEncodingCodec implements HttpUrlEncodingCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

/**
 * Determines if current navigation was a page reload
 * @param win
 */
export function isPageReload(win: Window): boolean {
  return (win.performance?.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming)?.type === 'reload';
}

/**
 * Regex applies to urls having a first path fragment consisting of two characters
 */
export const URL_LANG_PREFIX_REGEX = /(^\/[a-z]{2})(\/.+)?$/;

export function removeUrlLangPrefix(url: string): string {
  return url.replace(URL_LANG_PREFIX_REGEX, '$2') || '/';
}

/**
 * Returns URL object from a string.
 * In case of an error, return empty object for convenience
 * @param url
 */
export function getSafeURL(url: string): URL {
  try {
    return new URL(url);
  } catch (e) {
    return {} as URL;
  }
}
